import { ActionState } from 'services/EpdClient';
import styled from 'styled-components';

import useIsReadOnlyMode from '../compiler/hooks/useIsReadOnlyMode';
import SmartLabel, { TSmartLabelProps } from '../labels/SmartLabel';
import { useState } from 'react';
import { toaster } from '../toast';

type TProps = Partial<TSmartLabelProps> & {
  value: any;
  name: string;
  onChanged: any;
  disabled?: boolean;
  labelBoxStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  placeholder?: string;
  type?: string;
  containerClassName?: string;
  state?: any;
  maxLength?: number;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const WizardTextAreaComponent: React.FunctionComponent<TProps> = ({
  label,
  tooltip,
  tooltipPosition,
  labelStyle,
  labelBoxStyle,
  name,
  value,
  onChanged,
  disabled,
  style,
  placeholder,
  required,
  containerClassName,
  error,
  state,
  maxLength,
  ...props
}) => {
  const isReadOnly = useIsReadOnlyMode() || disabled || state === ActionState.Disabled;

  const handleChangeEvent = maxLength
    ? (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const inputText = e.target.value;
        if (inputText.length > maxLength) {
          toaster({
            severity: 'error',
            summary: `Maximum ${maxLength} characters allowed.`,
            details: 'Text exceeding this limit has been removed.',
          });
          e.target.value = inputText.substring(0, maxLength);
          return;
        }
      }
    : undefined;

  return (
    <div
      className={containerClassName}
      style={{
        width: '100%',
        display: state?.[name] === ActionState.Hidden ? 'none' : 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      {label && (
        <SmartLabel
          label={label}
          required={required}
          boxStyle={labelBoxStyle}
          labelStyle={labelStyle}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={!!value}
          error={error}
        />
      )}
      <StyledTextArea
        {...props}
        style={style}
        defaultValue={value}
        onBlur={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          e.currentTarget.value !== value && onChanged(name, e.currentTarget.value)
        }
        onChange={handleChangeEvent}
        disabled={isReadOnly}
        placeholder={isReadOnly ? undefined : placeholder}
      />
    </div>
  );
};

const StyledTextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  border-radius: 2px;
  margin: 0;
  resize: vertical;
  min-height: 67px;
  color: ${(props) => props.theme.colors.primaryBlack};

  &:focus {
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }

  &:focus-visible {
    outline: none;
  }

  &:hover:not([disabled]) {
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }

  &[disabled] {
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
    color: ${(props) => props.theme.colors.primaryBlack70};
    border-width: 0;
  }

  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  &::placeholder {
    color: ${(props) => props.theme.colors.primaryBlack50};
    opacity: 1;
  }
`;
export default WizardTextAreaComponent;
